import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { 
  FaShieldAlt, 
  FaLeaf, 
  FaIndustry, 
  FaUsers, 
  FaFlask, 
  FaCogs, 
  FaRecycle, 
  FaHeart, 
  FaGlobeAmericas,
  FaAward,
  FaChartLine,
  FaWater,
  FaSeedling,
  FaMicroscope,
  FaCertificate
} from 'react-icons/fa';
import './css/EHSSustainability.css';
import SEO from '../components/SEO';
import sustainabilityImage from '../assets/images/Laboratroy Image .jpg';
import greenChemistryIcons from '../assets/images/Additional Image -5.webp';
import labPlantsFuturistic from '../assets/images/Nimble Green Chemistry -2.jpg';
import plantTerrariums from '../assets/images/Nimble Green Chemistry -3 .jpg';
import molecularFlask from '../assets/images/Nimble Eco Solutions -1 .jpg';

// Green Chemistry Principle Images
import inheritanceImage from '../assets/images/inheritance.png';
import accidentImage from '../assets/images/acccident.png';
import derivativesImage from '../assets/images/derivatives.png';
import resourceImage from '../assets/images/res.png';
import auxiliariesImage from '../assets/images/auxilaries.png';
import synthesisImage from '../assets/images/synthesis.png';
import preventionImage from '../assets/images/prevention.png';
import catalysisImage from '../assets/images/catalyst.png';
import feedstocksImage from '../assets/images/feedstocks.png';
import efficiencyImage from '../assets/images/efficiency.png';

const EHSSustainability = () => {
  return (
    <>
      <SEO
        title="EHS & Sustainability - Environmental Health Safety | Nimble Group"
        description="Explore Nimble Group's comprehensive EHS & Sustainability initiatives. Our commitment to environmental health, safety standards, and sustainable chemical manufacturing practices."
        keywords="Eco-friendly industrial cleaners, Green industrial degreaser, Biodegradable parts cleaner, Sustainable metal degreasing, Non-hazardous parts cleaner, Low-VOC industrial degreaser, Non-flammable industrial solvent, Safer solvent alternatives, Eco-friendly industrial parts washer fluid, Sustainable heavy-duty degreaser, Water-based degreaser for metal, Industrial eco-cleaner manufacturer, Non-hazardous industrial chemical vendors, Aqueous cleaners, Bio-remediation parts washer, Water-based cleaning chemicals, Bio-based industrial cleaners, Eco ultrasonic cleaner solution, Industrial immersion cleaner, Green Seal certified industrial cleaner, Non-toxic cleaning fluid, GHS compliant industrial cleaning, TCE replacement chemical, Acid free cleaner, TCE Alternative, Kerosene alternative, Organic cleaners, EHS chemicals, EHS solutions, OSHA approved cleaners, VOC-free cleaners, HAP-free cleaners, Waterless Hand cleaners, Acid alternatives, Solvent alternatives, Eco parts cleaners, Green solvents, Low-VOC cleaners, Green degreasers, Green cleaners, Chlorine free cleaner, Waterless Oil & grease removers, Non flammable cleaners, MDC Alternatives, Sustainable industrial cleaning solutions, Aqueous parts washer cleaner, Industrial cleaner for reduced VOCs, Phosphate-free industrial cleaner, OSHA-compliant industrial cleaner, EHS approved cleaning chemicals, Cleaner to replace Trichloroethylene, Industrial cleaning agents without HAPs, Non-toxic ultrasonic cleaner solution, Caustic free cleaner, Bio-based parts cleaner for assembly line, Biodegradable parts cleaner supplier for corporates, Wholesale eco-friendly industrial degreasers, Alkaline aqueous parts cleaner, Natural solvent degreaser, Microbial parts washer fluid, Neutral pH industrial degreaser, Aqueous ultrasonic degreaser concentrate, Environmentally friendly spray washer chemicals, EPA Safer Choice industrial products, REACH compliant cleaning chemicals, EPA Safe cleaner, Substitutes for xylene in industrial cleaning, Non-caustic industrial cleaner, Non corrosive cleaner, Non corrosive degreaser, Environment friendly chemicals, Sustainable cleaners, Eco cleaners, Eco friendly degreasers, Eco solutions, EHS sustainability, environmental health safety, sustainable manufacturing, green chemistry, environmental compliance, safety standards, sustainable business practices"
        canonical="/ehs-sustainability"
      />
      <EHSSustainabilityContent />
    </>
  );
};

const EHSSustainabilityContent = () => {
  const { scrollYProgress } = useScroll();
  const parallaxY1 = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const parallaxY2 = useTransform(scrollYProgress, [0, 1], [0, -50]);
  
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { 
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const greenChemistryPrinciples = [
    {
      image: inheritanceImage,
      title: "Inherently Safer Chemistry for Accident Prevention",
      description: "Designing chemical processes to prevent accidents and enhance safety"
    },
    {
      image: auxiliariesImage,
      title: "Safer Solvents and Auxiliaries",
      description: "Using safer chemical substances and reducing environmental impact"
    },
    {
      image: derivativesImage,
      title: "Reduce Derivatives",
      description: "Minimizing chemical derivatives to reduce waste and complexity"
    },
    {
      image: synthesisImage,
      title: "Less Hazardous Synthesis",
      description: "Developing safer synthetic methods and processes"
    },
    {
      image: preventionImage,
      title: "Prevention",
      description: "Preventing waste rather than treating or cleaning up after it's created"
    },
    {
      image: resourceImage,
      title: "Design for Energy Efficiency",
      description: "Minimizing energy requirements in chemical processes"
    },
    {
      image: accidentImage,
      title: "Inherently Safer Chemistry for Accident Prevention",
      description: "Building safety into the fundamental design of chemical processes"
    },
    {
      image: catalysisImage,
      title: "Catalysis",
      description: "Using catalytic processes to increase efficiency and reduce waste"
    },
    {
      image: feedstocksImage,
      title: "Use of Renewable Feedstocks",
      description: "Utilizing renewable raw materials instead of depleting finite resources"
    },
    {
      image: efficiencyImage,
      title: "Design for Energy Efficiency",
      description: "Optimizing chemical processes to minimize energy consumption"
    }
  ];

  const facilityCommitments = [
    {
      icon: <FaFlask />,
      title: "Green Chemistry Integration",
      description: "Every product is conceived, designed, and produced in accordance with the 12 Green Chemistry principles, ensuring reduced toxicity, waste minimization, process safety, and responsible handling."
    },
    {
      icon: <FaShieldAlt />,
      title: "Clean, Safe, and Operator-Friendly Formulations",
      description: "Our specialty chemical formulations are VOC-free, non-chlorinated, phosphate-free, sulphate-free, and devoid of other toxic or regulated substances, delivering high operator safety and environmental integrity."
    },
    {
      icon: <FaCogs />,
      title: "Resource-Efficient, Low-Emission Manufacturing",
      description: "Our production processes are optimized for minimal consumption of water, energy, and raw materials, actively reducing our carbon and operational footprint while sustaining high productivity."
    },
    {
      icon: <FaRecycle />,
      title: "Bio-Renewable and Sustainable Sourcing",
      description: "Wherever possible, we substitute petroleum-based ingredients with bio-based, renewable, and biodegradable raw materials, directly contributing to reduced CO₂ emissions and sustainable material cycles."
    }
  ];

  const cleaningTechnologies = [
    {
      icon: <FaSeedling />,
      title: "Bio-Based and Biodegradable Solvents",
      description: "Derived from renewable plant sources, these solvents are effective, low-toxicity, and environmentally benign, with minimal waste treatment requirements."
    },
    {
      icon: <FaWater />,
      title: "Aqueous-Based Cleaners",
      description: "Water-based formulations enriched with surfactants, chelating agents, and corrosion inhibitors, offering efficient removal of oils, greases, and soils without harsh caustics or acids."
    },
    {
      icon: <FaShieldAlt />,
      title: "pH-Neutral and Mildly Alkaline Cleaners",
      description: "These formulations reduce the risks associated with corrosive substances while maintaining high-performance cleaning."
    },
    {
      icon: <FaCertificate />,
      title: "Non-Toxic Corrosion Inhibitors",
      description: "Critical for preventing flash rust and oxidation on cleaned ferrous and non-ferrous parts, without introducing new hazards."
    },
    {
      icon: <FaLeaf />,
      title: "Low-VOC and VOC-Free Solutions",
      description: "Compliant with stringent EPA guidelines, reducing air pollution and occupational exposure."
    }
  ];

  const businessAdvantages = [
    "Regulatory Compliance: Aligning with OSHA, EPA, and NTA standards, our solutions reduce workplace hazards and environmental emissions.",
    "Enhanced Operator Safety: Reduced exposure risks, improved indoor air quality, and minimized PPE requirements contribute to a safer, healthier work environment.",
    "Lower Waste Disposal Costs: Eco-cleaners generate less toxic waste, simplify disposal logistics, and reduce long-term environmental liabilities.",
    "Process Efficiency & Equipment Longevity: Less abrasive, non-corrosive formulations protect both parts and cleaning equipment, extending their service life.",
    "Sustainable Operations: Transitioning to eco-friendly cleaning processes supports corporate sustainability objectives, reduces carbon footprint, and enhances brand reputation."
  ];

  const ehsBenefits = [
    "Minimized hazardous material storage and handling risks",
    "Improved worker health and safety outcomes",
    "Positive impact on surrounding communities and public perception",
    "Compliance with global green manufacturing norms and ISO 14001 frameworks"
  ];

  const processConsiderations = [
    "Material Compatibility: Every formulation is tested for compatibility with different metallurgies — steel, aluminum, copper, brass, and special alloys — to prevent surface degradation.",
    "Contaminant-Specific Solutions: From oils, greases, and metal oxides to particulates and machining residues, our eco-cleaners are customized to target specific contaminants while minimizing environmental impact.",
    "Optimized Efficiency: Our formulations achieve effective cleaning at lower concentrations, with faster cycles and reduced energy requirements.",
    "Minimal Waste Generation: Designed for easier recycling and disposal, significantly lowering hazardous waste treatment costs.",
    "Industry-Customized Solutions: We tailor solutions to unique operational needs across automotive, electrical, electronics, and component manufacturing, using spray, immersion, ultrasonic, or vapor degreasing methods."
  ];

  const frameworkPillars = [
    {
      icon: <FaShieldAlt />,
      title: "Safe Products",
      description: "Every formulation prioritizes operator safety, emissions reduction, and environmentally responsible end-of-life characteristics."
    },
    {
      icon: <FaIndustry />,
      title: "Safe Workplaces",
      description: "We enforce comprehensive EHS protocols, including risk assessments, employee training, audits, and emergency preparedness."
    },
    {
      icon: <FaUsers />,
      title: "Safe Communities",
      description: "By championing green manufacturing practices among our customers and vendors, we foster resilient, responsible industrial ecosystems."
    }
  ];

  const impactStats = [
    { icon: <FaAward />, value: "14", label: "OEM customers transitioned from solvent-based to eco-cleaners" },
    { icon: <FaChartLine />, value: "21", label: "customized sustainable solutions implemented successfully" },
    { icon: <FaCertificate />, value: "4", label: "patented eco-products developed, reducing hazardous chemical dependency" },
    { icon: <FaLeaf />, value: "100%", label: "mineral oil-free and solvent-free additives for metalworking fluids" }
  ];

  return (
    <div className="ehs-page">
      {/* Background Pattern */}
      <div className="background-pattern"></div>
      
      {/* Header Section with Parallax */}
      <motion.div 
        className="page-header"
        style={{ y: parallaxY1 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="header-content">
          <h1 className="section-title">EHS & Sustainability</h1>
          <motion.p 
            className="hero-subtitle"
            variants={itemVariants}
          >
            Responsibility Beyond Compliance. Innovation Beyond Convention.
          </motion.p>
        </div>
        
        {/* Green Chemistry Principles Bubbles - Only show on desktop */}
        {!isMobile && (
          <div className="principles-bubbles-container">
            <div className="principles-bubbles">
              {[...greenChemistryPrinciples, ...greenChemistryPrinciples, ...greenChemistryPrinciples].map((principle, index) => (
                <motion.div
                  key={index}
                  className="principle-bubble"
                  variants={itemVariants}
                  whileHover={{ scale: 1.1, y: -5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="bubble-image">
                    <img src={principle.image} alt={principle.title} />
                  </div>
                  <div className="bubble-tooltip">
                    <h4>{principle.title}</h4>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </motion.div>

      {/* Introduction Section */}
      <motion.section 
        className="ehs-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <motion.div className="intro-content" variants={itemVariants}>
          <p className="intro-text content-justified">
            At Nimble Technologies, Environment, Health & Safety (EHS) and Sustainability are not ancillary functions — they form the core philosophy that drives our product development, operational protocols, and customer partnerships. As a forward-looking specialty chemical manufacturer, we believe every formulation and industrial process should serve a larger purpose: protecting human life, preserving ecological resources, and reducing operational hazards — without compromising productivity.
          </p>
          
          <p className="intro-text content-justified">
            Guided by the globally recognized 12 Principles of Green Chemistry, our operations and product innovations are built to uphold the highest environmental and safety standards. We recognize that while financial costs are quantifiable, the value of human well-being and ecological health is immeasurable. This conviction underpins every product we engineer, every process we optimize, and every industrial problem we solve.
          </p>

          <motion.div className="green-chemistry-showcase" variants={itemVariants}>
            <img src={greenChemistryIcons} alt="Green Chemistry Principles and Sustainable Laboratory Practices" />
            <div className="showcase-caption">
              <h3>Green Chemistry at the Core</h3>
              <p>Our comprehensive approach integrates sustainable practices across all chemical formulations and processes.</p>
            </div>
          </motion.div>
        </motion.div>
      </motion.section>

      {/* EHS Framework Section */}
      <motion.section 
        className="ehs-section alt-bg"
        style={{ y: parallaxY2 }}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <motion.div className="section-header" variants={itemVariants}>
          <div className="icon-container">
            <FaGlobeAmericas />
          </div>
          <h2>Our EHS & Sustainability Commitment Framework</h2>
        </motion.div>
        
        <motion.p className="content-justified" variants={itemVariants}>
          Our commitment to EHS and sustainability is reflected both at our manufacturing facilities and at our customers' operations, wherever our products and solutions are applied.
        </motion.p>

        <motion.div className="framework-pillars" variants={containerVariants}>
          {frameworkPillars.map((pillar, index) => (
            <motion.div 
              key={index}
              className="pillar-card"
              variants={itemVariants}
              whileHover={{ y: -5 }}
            >
              <div className="pillar-icon">
                {pillar.icon}
              </div>
              <h4>{pillar.title}</h4>
              <p>{pillar.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </motion.section>

      {/* Manufacturing Facilities Section */}
      <motion.section 
        className="ehs-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <motion.div className="facilities-visual" variants={itemVariants}>
          <img src={labPlantsFuturistic} alt="Advanced Green Chemistry Laboratory" />
          <div className="visual-overlay">
            <h3>State-of-the-Art Green Manufacturing</h3>
            <p>Where innovation meets environmental responsibility</p>
          </div>
        </motion.div>

        <motion.div 
          className="commitments-grid"
          variants={containerVariants}
        >
          {facilityCommitments.map((commitment, index) => (
            <motion.div 
              key={index}
              className="commitment-card"
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
            >
              <div className="commitment-icon">
                {commitment.icon}
              </div>
              <h4>{commitment.title}</h4>
              <p>{commitment.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </motion.section>

      {/* Customer Operations Section */}
      <motion.section 
        className="ehs-section alt-bg"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <motion.div className="section-header" variants={itemVariants}>
          <div className="icon-container">
            <FaUsers />
          </div>
          <h2>At Our Customers' Operations</h2>
        </motion.div>
        
        <motion.p className="content-justified" variants={itemVariants}>
          Our responsibility extends beyond our own boundaries. As innovators in eco-friendly specialty chemicals, we actively assist our customers in improving their EHS performance and sustainability benchmarks — not through consultancy services, but by understanding their technical challenges and delivering tailor-made, high-performance chemical solutions that elevate safety and environmental outcomes.
        </motion.p>
      </motion.section>

      {/* Eco-Friendly Cleaning Section */}
      <motion.section 
        className="ehs-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <motion.div className="section-header" variants={itemVariants}>
          <div className="icon-container green">
            <FaRecycle />
          </div>
          <h2>Eco-Friendly Cleaning Processes: Redefining Industrial Cleaning Standards</h2>
        </motion.div>
        
        <motion.div className="cleaning-intro" variants={itemVariants}>
          <p className="content-justified">
            Eco-friendly industrial cleaning is no longer an option — it's a business imperative aligned with OSHA, EPA, and NTA guidelines. By adopting these sustainable processes, industries can not only enhance workplace safety and environmental responsibility but also optimize operational costs and regulatory compliance.
          </p>
          
          <p className="content-justified">
            As experienced technocrats specializing in eco-cleaning chemicals, we help customers transition away from hazardous, legacy cleaning agents — including high-pH caustics, strong acids, and chlorinated solvents — towards customized, safer alternatives without compromising process effectiveness.
          </p>
        </motion.div>
      </motion.section>

      {/* Why Eco-Friendly Cleaning Section */}
      <motion.section 
        className="ehs-section alt-bg"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <motion.div className="section-header" variants={itemVariants}>
          <div className="icon-container">
            <FaMicroscope />
          </div>
          <h2>Why Eco-Friendly Cleaning Is Critical for Industry</h2>
        </motion.div>
        
        <motion.p className="content-justified" variants={itemVariants}>
          Industrial cleaning and degreasing are essential for parts preparation in operations like coating, machining, and assembly. Traditionally, industries have relied on harsh chemicals such as sodium hydroxide, hydrochloric acid, and chlorinated solvents, which pose significant risks to human health, environmental safety, and downstream systems.
        </motion.p>
        
        <motion.p className="content-justified" variants={itemVariants}>
          Eco-cleaning processes, based on green chemistry and sustainable methodologies, offer safer, effective, and regulatory-compliant solutions, minimizing ecological harm, protecting operator health, and supporting environmentally responsible manufacturing.
        </motion.p>
      </motion.section>

      {/* Eco-Friendly Technologies Section */}
      <motion.section 
        className="ehs-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <motion.div className="section-header" variants={itemVariants}>
          <div className="icon-container green">
            <FaCogs />
          </div>
          <h2>Eco-Friendly Cleaning Technologies: Modern, Safe, and Compliant</h2>
        </motion.div>
        
        <motion.p className="content-justified" variants={itemVariants}>
          Our eco-cleaning technologies are engineered to deliver outstanding performance without the hazards of traditional chemicals. Core components include:
        </motion.p>

        <motion.div 
          className="technologies-grid"
          variants={containerVariants}
        >
          {cleaningTechnologies.map((tech, index) => (
            <motion.div 
              key={index}
              className="technology-card"
              variants={itemVariants}
              whileHover={{ y: -3 }}
            >
              <div className="tech-icon">
                {tech.icon}
              </div>
              <h4>{tech.title}</h4>
              <p>{tech.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </motion.section>

      {/* Process Design Section */}
      <motion.section 
        className="ehs-section alt-bg"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <motion.div className="section-header" variants={itemVariants}>
          <div className="icon-container">
            <FaFlask />
          </div>
          <h2>Process Design: Tailored Eco-Cleaning Solutions</h2>
        </motion.div>
        
        <motion.p className="content-justified" variants={itemVariants}>
          Designing an effective, eco-friendly cleaning process requires a nuanced understanding of both materials and contaminant profiles.
        </motion.p>

        <motion.h3 variants={itemVariants}>Key Process Considerations:</motion.h3>
        
        <motion.ul 
          className="process-list content-justified"
          variants={containerVariants}
        >
          {processConsiderations.map((consideration, index) => (
            <motion.li 
              key={index} 
              variants={itemVariants}
              whileHover={{ x: 5 }}
            >
              <div className="bullet-point"></div>
              {consideration}
            </motion.li>
          ))}
        </motion.ul>
      </motion.section>

      {/* Business Advantages Section */}
      <motion.section 
        className="ehs-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <motion.div className="section-header" variants={itemVariants}>
          <div className="icon-container green">
            <FaChartLine />
          </div>
          <h2>Business Advantages of Eco-Friendly Cleaning</h2>
        </motion.div>
        
        <motion.p className="content-justified" variants={itemVariants}>
          Implementing eco-cleaning processes delivers tangible operational, regulatory, and environmental benefits:
        </motion.p>

        <motion.ul 
          className="advantages-list content-justified"
          variants={containerVariants}
        >
          {businessAdvantages.map((advantage, index) => (
            <motion.li 
              key={index} 
              variants={itemVariants}
              whileHover={{ x: 5 }}
            >
              <div className="bullet-point green"></div>
              {advantage}
            </motion.li>
          ))}
        </motion.ul>
      </motion.section>

      {/* EHS Management Section */}
      <motion.section 
        className="ehs-section alt-bg"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <motion.div className="section-header" variants={itemVariants}>
          <div className="icon-container">
            <FaShieldAlt />
          </div>
          <h2>EHS Management and Eco-Friendly Cleaning</h2>
        </motion.div>
        
        <motion.p className="content-justified" variants={itemVariants}>
          Modern EHS management demands processes that actively reduce chemical hazards and environmental risks. Eco-cleaning solutions play a pivotal role in this transition.
        </motion.p>

        <motion.h3 variants={itemVariants}>EHS Benefits:</motion.h3>
        
        <motion.ul 
          className="ehs-benefits-list content-justified"
          variants={containerVariants}
        >
          {ehsBenefits.map((benefit, index) => (
            <motion.li 
              key={index} 
              variants={itemVariants}
              whileHover={{ x: 5 }}
            >
              <div className="bullet-point blue"></div>
              {benefit}
            </motion.li>
          ))}
        </motion.ul>
      </motion.section>

      {/* Impact Highlights Section */}
      <motion.section 
        className="ehs-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <motion.div className="section-header" variants={itemVariants}>
          <div className="icon-container green">
            <FaAward />
          </div>
          <h2>Impact Highlights</h2>
        </motion.div>
        
        <motion.div 
          className="impact-stats"
          variants={containerVariants}
        >
          {impactStats.map((stat, index) => (
            <motion.div 
              key={index}
              className="impact-stat"
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
            >
              <div className="stat-icon">
                {stat.icon}
              </div>
              <div className="stat-value">{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </motion.section>

      {/* CTA Section */}
      <motion.section 
        className="cta-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <motion.div className="cta-content" variants={itemVariants}>
          <h2>Ready to Transform Your Operations?</h2>
          <p>Partner with us to implement sustainable, safe, and effective EHS solutions that drive both environmental responsibility and operational excellence.</p>
          <div className="cta-tagline">
            "Innovation Beyond Convention. Responsibility Beyond Compliance."
          </div>
        </motion.div>
      </motion.section>
    </div>
  );
};

export default EHSSustainability; 